import { useParams, Link, Navigate } from "react-router-dom";
import { PROGRAMS } from "@/data/programs";
import ProgramImage from "@/components/ProgramImage";
import { ArrowLeft, Check, Heart, ArrowRight } from "lucide-react";

export default function ProgramDetail() {
  const { slug } = useParams();
  const program = PROGRAMS.find((p) => p.slug === slug);
  if (!program) return <Navigate to="/programs" replace />;

  const others = PROGRAMS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div data-testid="program-detail-page">
      <section className="relative isolate overflow-hidden">
        <img src={program.image} alt={program.name} style={{ filter: program.filter }} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-brandnavy/70" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28">
          <Link to="/programs" data-testid="back-to-programs" className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All Programs
          </Link>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black tracking-tight text-white">{program.name}</h1>
          <p className="mt-3 text-lg font-medium text-slate-200">{program.tag}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-2xl font-black text-brandnavy">About this program</h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{program.description}</p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Like every LP Foundation program, {program.name} begins with structured performance readiness — building the
            focus, discipline, and confidence students need to fully engage and grow.
          </p>
          {program.video && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-bold text-brandnavy">Watch {program.name} in action</h3>
              <video
                data-testid="program-video"
                controls
                playsInline
                preload="metadata"
                poster={program.poster}
                className="mt-4 w-full max-w-sm rounded-3xl border border-slate-100 bg-black shadow-sm"
              >
                <source src={program.video} type="video/mp4" />
              </video>
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm">
            <h3 className="font-display text-lg font-bold text-brandnavy">What students build</h3>
            <ul className="mt-5 space-y-3">
              {program.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full" style={{ background: `${program.color}22`, color: program.color }}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {o}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="program-enroll-button"
              className="mt-7 flex items-center justify-center gap-2 rounded-full bg-brandnavy px-6 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5"
            >
              Enroll / Ask About This Program
            </Link>
            <Link
              to="/support"
              data-testid="program-donate-button"
              className="mt-3 flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-orange-400"
            >
              <Heart className="h-4 w-4 fill-current" /> Support This Program
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="font-display text-2xl font-black text-brandnavy">More programs</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/programs/${p.slug}`}
                data-testid={`related-program-${p.slug}`}
                className="group overflow-hidden rounded-3xl bg-white border border-slate-100 shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-xl"
              >
                <div className="h-40 overflow-hidden">
                  <ProgramImage program={p} className="transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-bold text-brandnavy group-hover:text-accent transition-colors">{p.name}</h3>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brandnavy">
                    Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
